// =============================
// File: src/components/admin/AdminDocsManager.jsx
// =============================
import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  FolderOpen,
  FileText,
  Star,
  StarOff,
  Search,
  ArrowLeft,
  Settings,
  Eye
} from 'lucide-react';
import axios from 'axios';
import DocEditor from './DocEditor';
import CategoryManager from './CategoryManager';

const API_URL = process.env.REACT_APP_API_URL;

function AdminDocsManager({ setActiveComponent }) {
  const [view, setView] = useState('list'); // 'list', 'edit', 'categories'
  const [categories, setCategories] = useState([]);
  const [docs, setDocs] = useState([]);
  const [featuredDocs, setFeaturedDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setIsLoading(true);
      const [categoriesRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/docs/categories`),
        axios.get(`${API_URL}/api/docs`)
      ]);

      setCategories(categoriesRes.data);
      setDocs(docsRes.data);
      setFeaturedDocs(docsRes.data.filter(d => d.is_featured).sort((a, b) => a.featured_order - b.featured_order));
    } catch (error) {
      console.error('Error fetching docs:', error);
      alert('Error al cargar documentos');
    } finally {
      setIsLoading(false);
    }
  }

  function handleCreateNew() {
    setSelectedDoc(null);
    setView('edit');
  }

  function handleEditDoc(doc) {
    setSelectedDoc(doc);
    setView('edit');
  }

  async function handleDeleteDoc(docId) {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;

    try {
      await axios.delete(`${API_URL}/api/docs/${docId}`);
      alert('Documento eliminado exitosamente');
      fetchData();
    } catch (error) {
      console.error('Error deleting doc:', error);
      alert('Error al eliminar documento');
    }
  }

  async function handleToggleFeatured(doc) {
    try {
      const newFeaturedStatus = !doc.is_featured;
      
      if (newFeaturedStatus && featuredDocs.length >= 5) {
        alert('Máximo 5 documentos destacados permitidos');
        return;
      }

      await axios.put(`${API_URL}/api/docs/${doc.id}`, {
        ...doc,
        is_featured: newFeaturedStatus,
        featured_order: newFeaturedStatus ? featuredDocs.length + 1 : null
      });

      alert(`Documento ${newFeaturedStatus ? 'destacado' : 'removido de destacados'}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert('Error al actualizar documento');
    }
  }

  function handleBackToList() {
    setView('list');
    setSelectedDoc(null);
    fetchData();
  }

  // Filter docs
  const filteredDocs = docs.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.short_description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || doc.role_access === filterRole || doc.role_access === 'all';
    return matchesSearch && matchesRole;
  });

  // Group by category
  const docsByCategory = categories.map(cat => ({
    ...cat,
    docs: filteredDocs.filter(d => d.category_id === cat.id)
  })).filter(cat => cat.docs.length > 0);

  if (view === 'edit') {
    return (
      <DocEditor
        doc={selectedDoc}
        categories={categories}
        onBack={handleBackToList}
        onSave={handleBackToList}
      />
    );
  }

  if (view === 'categories') {
    return (
      <CategoryManager
        categories={categories}
        onBack={() => setView('list')}
        onUpdate={fetchData}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-pink-600 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActiveComponent("OptionsAdmin")}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-extrabold text-white font-montserrat">
                    Gestión de Documentación
                  </h1>
                  <p className="text-sm text-white/80 font-montserrat">
                    {docs.length} documentos • {featuredDocs.length}/5 destacados
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setView('categories')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Categorías</span>
              </button>
              <button
                onClick={handleCreateNew}
                className="px-4 py-2 bg-white text-brand-primary hover:bg-white/90 rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2 shadow-lg"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Nuevo Documento</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar documentos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
              />
            </div>

            {/* Role Filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
            >
              <option value="all">Todos los roles</option>
              <option value="customer">Usuarios</option>
              <option value="driver">Conductores</option>
              <option value="provider">Proveedores</option>
              <option value="admin">Administradores</option>
            </select>
          </div>
        </div>

        {/* Featured Docs Section */}
        {featuredDocs.length > 0 && (
          <div className="bg-gradient-to-br from-brand-gold/10 to-brand-primary/10 rounded-xl border-2 border-brand-gold/30 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-brand-gold fill-brand-gold" />
              <h2 className="text-lg font-bold text-slate-900 font-montserrat">
                Documentos Destacados ({featuredDocs.length}/5)
              </h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {featuredDocs.map((doc, index) => (
                <div
                  key={doc.id}
                  className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-bold text-brand-gold bg-brand-gold/10 px-2 py-1 rounded">
                      #{index + 1}
                    </span>
                    <button
                      onClick={() => handleToggleFeatured(doc)}
                      className="p-1 hover:bg-slate-100 rounded transition-colors"
                    >
                      <StarOff className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <h3 className="font-semibold text-slate-900 font-montserrat text-sm mb-1 line-clamp-2">
                    {doc.title}
                  </h3>
                  <p className="text-xs text-slate-600 font-montserrat line-clamp-2">
                    {doc.short_description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-slate-100 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {docsByCategory.map((category) => (
              <div key={category.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <FolderOpen className="w-5 h-5 text-brand-primary" />
                    <h2 className="text-lg font-bold text-slate-900 font-montserrat">
                      {category.name}
                    </h2>
                    <span className="text-sm text-slate-500 font-montserrat">
                      ({category.docs.length})
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-slate-100">
                  {category.docs.map((doc) => (
                    <div
                      key={doc.id}
                      className="px-6 py-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            <h3 className="font-semibold text-slate-900 font-montserrat truncate">
                              {doc.title}
                            </h3>
                            {doc.is_featured && (
                              <Star className="w-4 h-4 text-brand-gold fill-brand-gold flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-slate-600 font-montserrat line-clamp-2 ml-8">
                            {doc.short_description}
                          </p>
                          <div className="flex items-center gap-3 mt-2 ml-8">
                            <span className="text-xs text-slate-500 font-montserrat">
                              Rol: <span className="font-semibold">{getRoleLabel(doc.role_access)}</span>
                            </span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-500 font-montserrat">
                              Actualizado: {new Date(doc.updated_at).toLocaleDateString('es-PE')}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => window.open(`/#/user-docs/${doc.slug}`, '_blank')}
                            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            title="Vista previa"
                          >
                            <Eye className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => handleToggleFeatured(doc)}
                            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            title={doc.is_featured ? 'Quitar de destacados' : 'Destacar'}
                          >
                            {doc.is_featured ? (
                              <StarOff className="w-4 h-4 text-brand-gold" />
                            ) : (
                              <Star className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEditDoc(doc)}
                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteDoc(doc.id)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {docsByCategory.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 font-montserrat mb-2">
                  No hay documentos
                </h3>
                <p className="text-slate-600 font-montserrat mb-6">
                  {searchQuery ? 'No se encontraron resultados para tu búsqueda' : 'Comienza creando tu primer documento'}
                </p>
                <button
                  onClick={handleCreateNew}
                  className="px-6 py-3 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors"
                >
                  Crear Documento
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function getRoleLabel(role) {
  const labels = {
    customer: 'Usuarios',
    driver: 'Conductores',
    provider: 'Proveedores',
    admin: 'Administradores',
    all: 'Todos'
  };
  return labels[role] || role;
}

export default AdminDocsManager;